import { z } from 'zod';
import type { Rarity } from '../rarity';

/**
 * Combat pets.
 *
 * A pet is a real, persistent inventory instance that fights beside the ship.
 * It is NOT an artifact and NOT equipment in the loadout sense: it occupies a
 * slot of its own, exactly one per account may be active, and it never takes
 * damage, blocks an enemy or shields anything.
 *
 * Baby Aliens are deliberately not pets and never will be - they are permanent
 * inventory bonuses, and Other Game's chance modifier still produces nothing.
 *
 * ## What lives here
 *
 * The identity and the shape of a pet: which rarities exist, how a star rating
 * maps onto one, how many evolutions a pet has, and how big each form is. What
 * a pet *does* in combat is `packages/game-domain/src/hunt/pet-balance.ts` -
 * the domain owns damage, cadence and targeting, and this package stays free
 * of them.
 *
 * ## Retirement
 *
 * A definition is never deleted. `RETIRED_PET_IDS` names the ones that have
 * left the game: they stay parseable so old history reads, and every path that
 * could bring one back - the catalog, a grant, an activation, an evolution -
 * refuses them by id. See `docs/PETS_V1_SPEC.md`.
 */

/* -------------------------------------------------------------------------- */
/*  Stars                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Pets exist ONLY in the top five rarities, and the star rating is not a
 * second opinion about them - it is the same fact written two ways.
 *
 * This table is the single place the correspondence is declared. A pet with a
 * rarity outside it cannot be constructed, and a star count that disagrees
 * with the rarity is rejected rather than displayed.
 */
export const PET_RARITY_BY_STARS: Readonly<Record<PetStars, PetRarity>> = Object.freeze({
  1: 'legendary',
  2: 'excellent',
  3: 'perfect',
  4: 'anomaly',
  5: 'prismatic',
});

/** The five ratings a pet can have. Five is the maximum, always. */
export const PET_STAR_VALUES = [1, 2, 3, 4, 5] as const;
export type PetStars = (typeof PET_STAR_VALUES)[number];

/** Every star slot a pet card draws, filled or empty. */
export const PET_STAR_SLOTS = 5;

/** The rarities a pet may have. Common through Mythic pets do not exist. */
export const PET_RARITIES = [
  'legendary',
  'excellent',
  'perfect',
  'anomaly',
  'prismatic',
] as const satisfies readonly Rarity[];
export type PetRarity = (typeof PET_RARITIES)[number];

export const petRaritySchema = z.enum(PET_RARITIES);
export const petStarsSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
]);

export function isPetRarity(value: string): value is PetRarity {
  return (PET_RARITIES as readonly string[]).includes(value);
}

/** The rating a rarity implies. Throws for a rarity no pet can have. */
export function starsForPetRarity(rarity: Rarity): PetStars {
  const found = PET_STAR_VALUES.find((stars) => PET_RARITY_BY_STARS[stars] === rarity);
  if (found === undefined) {
    throw new RangeError(
      `${rarity} is not a pet rarity; pets exist only as ${PET_RARITIES.join(', ')}`,
    );
  }
  return found;
}

/** True when a (rarity, stars) pair is the one the table declares. */
export function isValidPetRating(rarity: Rarity, stars: number): boolean {
  return (
    PET_STAR_VALUES.includes(stars as PetStars) && PET_RARITY_BY_STARS[stars as PetStars] === rarity
  );
}

/* -------------------------------------------------------------------------- */
/*  Evolutions                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Evolution is stored in the instance's `level` column.
 *
 * That is deliberate rather than convenient: the vault already enforces a
 * per-definition level cap in a database trigger, merging already consumes two
 * copies of the same level to make one of the next, and the append-only
 * history already records both sides. A separate "evolution" column would have
 * duplicated all of it and given the two a chance to disagree.
 *
 * Stars and evolution are independent: a 1-star pet is 1-star in every form.
 * The rarity never changes; only the form does.
 */
export const PET_MAX_EVOLUTION = 3;

/**
 * How much bigger each evolution is than the one before it.
 *
 * A form that fights twice as hard should look it, but 1.3 compounded put
 * evolution 3 at 1.69x - large enough that the MK III's technological circle
 * ran off the bottom of the arena and crowded the ship. 1.19 compounds to
 * 1.4161, which still reads as a clear step up per form while leaving the
 * circle room to sit around the body.
 *
 * The scale is PRESENTATION ONLY. Damage, cadence, projectile count, targeting
 * and every economic quantity are unaffected by it.
 */
export const PET_SCALE_PER_EVOLUTION = 1.19;

/** The size multiplier of a form, relative to evolution 1. */
export function petScaleForEvolution(evolution: number): number {
  const clamped = Math.max(1, Math.min(PET_MAX_EVOLUTION, Math.trunc(evolution)));
  return PET_SCALE_PER_EVOLUTION ** (clamped - 1);
}

/** `EV.1`, `EV.2`, `EV.3` - what the inventory badge shows. */
export function petEvolutionLabel(evolution: number): string {
  return `EV.${evolution}`;
}

export interface PetEvolutionForm {
  /** 1-based; equals the instance level. */
  readonly evolution: number;
  /** `MK I`, `MK II`, `MK III`. */
  readonly name: string;
  /**
   * How many projectiles leave the pet per volley, and therefore how many
   * hardpoints the model carries. The renderer reads this, the simulation
   * fires from it, and a test asserts the two agree.
   */
  readonly projectiles: number;
  /** Asset id of the 2D thumbnail for this form, in the generated manifest. */
  readonly assetKey: string;
  /** Short description shown in the pets tab. */
  readonly description: string;
}

/** How a pet delivers its damage. Only the butterfly may ever be a beam. */
export const PET_WEAPON_KINDS = ['projectile', 'explosive', 'homing', 'beam'] as const;
export type PetWeaponKind = (typeof PET_WEAPON_KINDS)[number];

export interface PetDefinition {
  /** Permanent definition id, matching the vault catalog. */
  readonly id: string;
  readonly name: string;
  readonly rarity: PetRarity;
  readonly stars: PetStars;
  /** One entry per evolution, ordered from evolution 1. */
  readonly forms: readonly PetEvolutionForm[];
  /** How many of this pet may be active at once. Always 1 today. */
  readonly maxActive: 1;
  readonly weapon: PetWeaponKind;
  /** The energy colour this pet is born with, unlocked from the start. */
  readonly defaultEnergyId: string;
  /** The chassis finish this pet is born with, unlocked from the start. */
  readonly defaultFinishId: string;
}

/* -------------------------------------------------------------------------- */
/*  Retired content                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Definitions that have LEFT the game.
 *
 * They are not deleted, and deleting them would be a mistake: `item_instances`
 * has a foreign key onto `item_definitions`, `inventory_events` records their
 * ids forever, and an append-only history that no longer parses is not a
 * history. Instead the row stays, disabled, and every gate below refuses the
 * id - so a retired pet cannot be granted, listed, activated or evolved, and
 * an old event still reads.
 */
export const RETIRED_PET_IDS: readonly string[] = Object.freeze([
  // The Mecha Guerreiro, retired in 4B.1 in favour of the five-pet line-up in
  // `docs/PETS_V1_SPEC.md`. Its art and model are gone; its history is not.
  'pet_mecha_warrior_anomaly',
]);

const RETIRED = new Set(RETIRED_PET_IDS);

/** True for a definition that has left the game and must never come back. */
export function isRetiredPetId(id: string): boolean {
  return RETIRED.has(id);
}

/* -------------------------------------------------------------------------- */
/*  The catalog                                                               */
/* -------------------------------------------------------------------------- */

/**
 * 1-star Legendary: the Sentinel Orb.
 *
 * A segmented technological sphere - pearl-white shell, a bright core, rings
 * that turn independently, and floating plates. It gains orbiting energy
 * spheres and a three-dimensional technological circle as it evolves, and it
 * fires small discrete bolts: one, then two, then three at a time.
 *
 * It NEVER fires a beam. Only the Void Butterfly may, and it does not exist
 * yet - see `docs/PETS_V1_SPEC.md`.
 */
export const PET_SENTINEL_ORB: PetDefinition = Object.freeze({
  id: 'pet_tech_sphere_legendary',
  name: 'Orbe Sentinela',
  rarity: 'legendary',
  stars: 1,
  maxActive: 1,
  weapon: 'projectile',
  defaultEnergyId: 'energy_celestial_white',
  defaultFinishId: 'finish_pearl_white',
  forms: Object.freeze([
    {
      evolution: 1,
      name: 'MK I',
      projectiles: 1,
      assetKey: 'pet_tech_sphere_mk1',
      description:
        'Uma esfera segmentada com núcleo energético e um emissor. Dispara um projétil por vez.',
    },
    {
      evolution: 2,
      name: 'MK II',
      projectiles: 2,
      assetKey: 'pet_tech_sphere_mk2',
      description:
        'Quatro esferas de energia em órbita e dois emissores. Mais anéis, placas e fendas emissivas. Dois projéteis simultâneos.',
    },
    {
      evolution: 3,
      name: 'MK III',
      projectiles: 3,
      assetKey: 'pet_tech_sphere_mk3',
      description:
        'Seis esferas orbitando em planos distintos, círculo tecnológico tridimensional, runas e segmentos energéticos. Três projéteis simultâneos. Forma final.',
    },
  ]),
});

/**
 * 2-star Excellent: the Abyssal Drone.
 *
 * A black alien drone with a purple void core. Where the Orb fires small bolts
 * at one enemy, the Drone lobs spheres of energy that DETONATE - the enemy hit
 * takes the direct damage and everything else standing in the blast takes a
 * share. That is its whole identity, and the reason its cadence is slower and
 * its per-ball damage higher.
 *
 * The id is the one `docs/PETS_V1_SPEC.md` locked, not a new one: the spec
 * fixes ids precisely so a definition cannot be renamed after rows reference
 * it. `docs` calls it the Drone Abissal and so does the game.
 */
export const PET_ABYSSAL_DRONE: PetDefinition = Object.freeze({
  id: 'pet_alien_drone_excellent',
  name: 'Drone Abissal',
  rarity: 'excellent',
  stars: 2,
  maxActive: 1,
  weapon: 'explosive',
  defaultEnergyId: 'energy_void_purple',
  defaultFinishId: 'finish_obsidian_black',
  forms: Object.freeze([
    {
      evolution: 1,
      name: 'EV. I',
      projectiles: 1,
      assetKey: 'pet_alien_drone_ev1',
      description:
        'Casco compacto de metal negro com um olho roxo e quatro tentáculos curtos. Um canhão central: uma bola de energia por salva, com explosão pequena.',
    },
    {
      evolution: 2,
      name: 'EV. II',
      projectiles: 2,
      assetKey: 'pet_alien_drone_ev2',
      description:
        'Carapaça alienígena com placas externas, seis tentáculos articulados e o núcleo mais exposto. Dois canhões simétricos, duas bolas simultâneas e explosão média.',
    },
    {
      evolution: 3,
      name: 'EV. III',
      projectiles: 3,
      assetKey: 'pet_alien_drone_ev3',
      description:
        'Coroa de lâminas curvas, oito tentáculos segmentados e veias do vazio por todo o casco. Três canhões em triângulo, três bolas simultâneas e a maior explosão. Forma final.',
    },
  ]),
});

/**
 * 3-star Perfect: the Ballistic Drone.
 *
 * A black military drone with yellow neon that fires GUIDED MISSILES. Each
 * missile picks its own enemy independently, so a volley may concentrate on one
 * target or scatter across several - which is the whole difference between it
 * and the Abyssal Drone, whose volley deliberately spreads and whose blasts
 * damage bystanders. A missile damages the enemy it hit and nobody else.
 *
 * The id is the one `docs/PETS_V1_SPEC.md` locked, not the one the prompt
 * suggested: that document fixes ids precisely so a definition cannot be
 * renamed after rows reference it.
 */
export const PET_BALLISTIC_DRONE: PetDefinition = Object.freeze({
  id: 'pet_missile_drone_perfect',
  name: 'Drone Balístico',
  rarity: 'perfect',
  stars: 3,
  maxActive: 1,
  weapon: 'homing',
  defaultEnergyId: 'energy_solar_yellow',
  defaultFinishId: 'finish_obsidian_black',
  forms: Object.freeze([
    {
      evolution: 1,
      name: 'EV. I',
      projectiles: 1,
      assetKey: 'pet_missile_drone_ev1',
      description:
        'Corpo compacto de metal negro com um lançador central, duas aletas estabilizadoras e um propulsor amarelo. Um míssil teleguiado por salva.',
    },
    {
      evolution: 2,
      name: 'EV. II',
      projectiles: 2,
      assetKey: 'pet_missile_drone_ev2',
      description:
        'Asas tecnológicas mais largas, dois lançadores laterais, duas turbinas e um radar amarelo giratório. Dois mísseis simultâneos, cada um com seu próprio alvo.',
    },
    {
      evolution: 3,
      name: 'EV. III',
      projectiles: 4,
      assetKey: 'pet_missile_drone_ev3',
      description:
        'Silhueta em X com quatro lançadores, quatro estabilizadores articulados, três turbinas, blindagem flutuante, coroa de rastreamento e retículos holográficos. Quatro mísseis simultâneos. Forma final.',
    },
  ]),
});

/**
 * 4-star Anomaly: the Void Butterfly.
 *
 * A black machine shaped after a Morpho, firing a CONTINUOUS LASER from the
 * sapphire at its chest. It is the single authorised exception to the project's
 * permanent prohibition on beams - `docs/PETS_V1_SPEC.md` grants it to this pet
 * and to nothing else, ever. No ship shot, no cannon, and none of the other
 * three pets may become one.
 *
 * Evolution buys FREQUENCY, never a second beam. There is exactly one beam in
 * every form; EV. I applies six damage instances a second, EV. II eight and
 * EV. III ten. `projectiles` is therefore 1 in all three forms - the field
 * means "how many things this pet emits at once", and the answer is one beam.
 *
 * The beam locks a single enemy, damages that enemy and nobody else, does not
 * pierce, does not chain and does not split.
 */
export const PET_VOID_BUTTERFLY: PetDefinition = Object.freeze({
  id: 'pet_void_butterfly_anomaly',
  name: 'Borboleta do Vazio',
  rarity: 'anomaly',
  stars: 4,
  maxActive: 1,
  weapon: 'beam',
  defaultEnergyId: 'energy_ion_blue',
  defaultFinishId: 'finish_obsidian_black',
  forms: Object.freeze([
    {
      evolution: 1,
      name: 'EV. I',
      projectiles: 1,
      assetKey: 'pet_void_butterfly_ev1',
      description:
        'Quatro asas mecânicas de placas negras angulares, largas para os lados, com dobradiças visíveis nas raízes e veias azuis. Cristal de safira central: um único laser contínuo, seis instâncias de dano por segundo.',
    },
    {
      evolution: 2,
      name: 'EV. II',
      projectiles: 1,
      assetKey: 'pet_void_butterfly_ev2',
      description:
        'Placas segmentadas, dupla articulação nas raízes, pistões e atuadores à mostra, quatro nós de energia azul e um retículo fino ao redor do cristal. O mesmo feixe único, agora com oito instâncias de dano por segundo.',
    },
    {
      evolution: 3,
      name: 'EV. III',
      projectiles: 1,
      assetKey: 'pet_void_butterfly_ev3',
      description:
        'Rainha mecânica do vazio: camadas de placas, asas superiores largas na horizontal, servos legíveis, blindagem flutuante, fragmentos de safira em órbita e halo tecnológico com marcações. Ainda um único feixe, com dez instâncias de dano por segundo. Forma final.',
    },
  ]),
});

/**
 * 5-star Prismatic: the Apache Omega.
 *
 * A robotic gunship, not a repainted Apache: obsidian armour, red neon running
 * its conduits, counter-rotating rotors and a tail that grows from one boom to
 * two to three. No pilot, no cockpit glass anyone sits behind - a black visor
 * and a machine.
 *
 * Its guns are the cannon 3 concept: each MACHINE GUN fires three parallel
 * projectiles at one enemy, so the forms deliver 3, 6 and 9 a volley from 1, 2
 * and 3 guns. `projectiles` below is the TOTAL per volley, which is what the
 * field has always meant; how many guns produce it is stated once, in the
 * domain's muzzle layout, and a test asserts the two multiply out.
 *
 * The id is the one `docs/PETS_V1_SPEC.md` locked - not the one the prompt
 * suggested. That document fixes ids precisely so a definition cannot be
 * renamed after rows reference it.
 */
export const PET_APACHE_OMEGA: PetDefinition = Object.freeze({
  id: 'pet_apache_prismatic',
  name: 'Apache Ômega',
  rarity: 'prismatic',
  stars: 5,
  maxActive: 1,
  weapon: 'projectile',
  defaultEnergyId: 'energy_plasma_red',
  defaultFinishId: 'finish_obsidian_black',
  forms: Object.freeze([
    {
      evolution: 1,
      name: 'EV. I',
      projectiles: 3,
      assetKey: 'pet_apache_ev1',
      description:
        'Helicóptero robótico de assalto com uma metralhadora tripla e cauda blindada única.',
    },
    {
      evolution: 2,
      name: 'EV. II',
      projectiles: 6,
      assetKey: 'pet_apache_ev2',
      description:
        'Estrutura reforçada, rotores contrarrotativos, cauda bifurcada e duas metralhadoras triplas.',
    },
    {
      evolution: 3,
      name: 'EV. III',
      projectiles: 9,
      assetKey: 'pet_apache_ev3',
      description:
        'Forma final. Três metralhadoras triplas, seis turbinas e cauda tríplice articulada.',
    },
  ]),
});

/**
 * Every pet that EXISTS today - all five of `docs/PETS_V1_SPEC.md`.
 */
export const PET_DEFINITIONS: readonly PetDefinition[] = Object.freeze([
  PET_SENTINEL_ORB,
  PET_ABYSSAL_DRONE,
  PET_BALLISTIC_DRONE,
  PET_VOID_BUTTERFLY,
  PET_APACHE_OMEGA,
]);

/**
 * The ONE pet allowed to fire a beam, by id.
 *
 * Declared as a set rather than left implicit in the weapon field, so the rule
 * is greppable and a test can assert that exactly one definition carries it.
 * Adding a second id here is a deliberate act that has to argue with
 * `docs/PETS_V1_SPEC.md`, which is the point.
 */
export const BEAM_AUTHORISED_PET_IDS: readonly string[] = Object.freeze([PET_VOID_BUTTERFLY.id]);

/** True only for the Void Butterfly. Every other shot in the game is discrete. */
export function isBeamAuthorisedPetId(id: string): boolean {
  return BEAM_AUTHORISED_PET_IDS.includes(id);
}

const PETS_BY_ID: ReadonlyMap<string, PetDefinition> = new Map(
  PET_DEFINITIONS.map((pet) => [pet.id, pet]),
);

/** A live pet by id. Retired ids resolve to null, by construction. */
export function findPet(id: string): PetDefinition | null {
  return PETS_BY_ID.get(id) ?? null;
}

export function isPetDefinitionId(id: string): boolean {
  return PETS_BY_ID.has(id);
}

/** The form at an evolution level, or null when the level is out of range. */
export function petFormAt(pet: PetDefinition, evolution: number): PetEvolutionForm | null {
  return pet.forms.find((form) => form.evolution === evolution) ?? null;
}

/** Throwing lookup for paths where an unknown form is a programming error. */
export function requirePetForm(pet: PetDefinition, evolution: number): PetEvolutionForm {
  const form = petFormAt(pet, evolution);
  if (!form) {
    throw new RangeError(`${pet.id} has no evolution ${evolution}`);
  }
  return form;
}

/* -------------------------------------------------------------------------- */
/*  Persistence                                                               */
/* -------------------------------------------------------------------------- */

/**
 * The active pet as a hunt session freezes it.
 *
 * Parsed before it is trusted, exactly like the loadout and the passive
 * summary: it is JSON in PostgreSQL, and a row that was corrupted, edited by
 * hand or written by an older build must fail loudly rather than quietly
 * producing a run nobody can reproduce.
 *
 * The training counters and the appearance ids live here too, because the
 * session re-simulates with the pet as it WAS - a pet fed mid-window did not
 * do that damage for the whole window.
 */
export const resolvedPetSchema = z.object({
  instanceId: z.uuid(),
  definitionId: z.string().min(1),
  evolution: z.number().int().min(1).max(PET_MAX_EVOLUTION),
  rarity: petRaritySchema,
  stars: petStarsSchema,
  /** Permanent training, per instance. Never negative, never fractional. */
  damageFoodCount: z.number().int().min(0),
  attackSpeedFoodCount: z.number().int().min(0),
  /** Appearance, per instance. Ids from `pet-appearance.ts`. */
  energyId: z.string().min(1),
  finishId: z.string().min(1),
  contentVersion: z.number().int().min(1),
});

export type ResolvedPetWire = z.infer<typeof resolvedPetSchema>;

/**
 * Parses a frozen pet and REFUSES one whose rating disagrees with itself.
 *
 * A row claiming four stars with a Perfect rarity is not a cosmetic problem -
 * it is two different claims about how rare the thing is, and a player would
 * be right to believe either. It fails loudly instead.
 */
export function parseResolvedPet(value: unknown): ResolvedPetWire {
  const parsed = resolvedPetSchema.parse(value);
  if (!isValidPetRating(parsed.rarity, parsed.stars)) {
    throw new RangeError(
      `pet ${parsed.definitionId} claims ${parsed.stars} stars with rarity ${parsed.rarity}`,
    );
  }
  return parsed;
}
